import './index.css'
import { FC, useContext, useMemo, useState } from 'react'
import { logout } from '../../services/admin-auth-service'
import { Avatar, Badge, ConfigProvider, Divider, Layout, Menu, Typography } from 'antd'
import { BrowserView, MobileView } from 'react-device-detect'
import { Link } from 'react-router-dom'
import {
  ApartmentOutlined,
  BellOutlined,
  HomeOutlined,
  LogoutOutlined,
  RightOutlined,
  UserOutlined,
} from '@ant-design/icons'
import { InfoCxt } from '../../App'
import SearchBar from '../Header/components/searchBar'

const { Sider } = Layout
const { Text } = Typography

export const MENU_KEYS = ['dashboard', 'patient', 'organizations', 'doctors']

interface PageSiderProps {
  selectedKey: string
  showProfile?: boolean
  removeCookie?: (name: 'loginToken', options?: { path?: string }) => void
  collapsed: boolean
}

export const PageSider: FC<PageSiderProps> = ({
  showProfile,
  selectedKey,
  removeCookie,
  collapsed: pinnedCollapsed,
}: PageSiderProps) => {
  const infoContext = useContext(InfoCxt)
  const { showComingSoon, username, firstName, avatar, role } = infoContext
  const displayName = username || firstName
  const notificationCount = 21
  const [hovering, setHovering] = useState(false)
  const collapsed = pinnedCollapsed && !hovering

  const handleMouseEnter = () => setHovering(true)
  const handleMouseLeave = () => setHovering(false)

  const menuItems = useMemo(
    () => [
      {
        key: 'dashboard',
        icon: <HomeOutlined />,
        label: (
          <Link style={{ display: 'inline-block' }} to='/'>
            <BrowserView>
              <span style={{ fontSize: '12px' }}>Home</span>
            </BrowserView>
            <MobileView>
              <span style={{ fontSize: '12px' }}>Home</span>
              <RightOutlined style={{ position: 'absolute', right: 16, top: 12 }} />
            </MobileView>
          </Link>
        ),
      },
      {
        key: 'patient',
        icon: <UserOutlined />,
        label: (
          <Link style={{ display: 'inline-block' }} to='/patient'>
            <BrowserView>
              <span style={{ fontSize: '12px' }}>View Patient</span>
            </BrowserView>
            <MobileView>
              <span style={{ fontSize: '12px' }}>View Patient</span>
              <RightOutlined style={{ position: 'absolute', right: 16, top: 12 }} />
            </MobileView>
          </Link>
        ),
      },
      ...(role === 'ROLE_ADMIN'
        ? [
            {
              key: 'organizations',
              icon: <ApartmentOutlined />,
              label: (
                <Link style={{ display: 'inline-block' }} to='/organizations'>
                  <BrowserView>
                    <span style={{ fontSize: '12px' }}>Manage Organizations</span>
                  </BrowserView>
                  <MobileView>
                    <span style={{ fontSize: '12px' }}>Manage Organizations</span>
                    <RightOutlined style={{ position: 'absolute', right: 16, top: 12 }} />
                  </MobileView>
                </Link>
              ),
            },
          ]
        : []),
      ...(role === 'ROLE_ORGANIZATION'
        ? [
            {
              key: 'doctors',
              icon: <UserOutlined />,
              label: (
                <Link style={{ display: 'inline-block' }} to='/doctors'>
                  <BrowserView>
                    <span style={{ fontSize: '12px' }}>Manage Doctors</span>
                  </BrowserView>
                  <MobileView>
                    <span style={{ fontSize: '12px' }}>Manage Doctors</span>
                    <RightOutlined style={{ position: 'absolute', right: 16, top: 12 }} />
                  </MobileView>
                </Link>
              ),
            },
          ]
        : []),
    ],
    [role]
  )

  const profileMenuItems = useMemo(
    () => [
      {
        key: 'profile',
        icon: <UserOutlined />,
        label: (
          <Link style={{ display: 'inline-block' }} to='/settings/profile'>
            <span>Profile</span>
          </Link>
        ),
      },
      {
        key: 'notification',
        icon: <BellOutlined />,
        label: (
          <span>
            <span>Notification</span>
            <div style={{ display: 'inline-block' }}>
              <div className='notification-badge'>
                <Badge count={notificationCount} />
              </div>
            </div>
          </span>
        ),
      },
    ],
    []
  )

  return (
    <>
      {/* PC version */}
      <BrowserView>
        <Sider
          collapsed={collapsed}
          style={{ width: collapsed ? 80 : 264, height: '100vh', transition: 'width 0.3s' }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}>
          <div className='page-sider'>
            <a
              href='/'
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                padding: '20px 0',
                alignItems: 'center',
                cursor: 'pointer',
              }}>
              {collapsed ?
                <img src='/assets/icon/engage-bw.svg' alt='logo' width={48} height={48} /> :
                <img src='/assets/icon/engage-bw.svg' alt='logo' width={100} height={48} />
              }
              {/*!collapsed && (
                <Text
                  style={{
                    color: '#fff',
                    fontSize: 14,
                    fontWeight: 600,
                    padding: '0 8px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  className='sider-title'>
                  engage
                </Text>
              )*/}
            </a>
            <Menu selectedKeys={selectedKey ? [selectedKey] : ['dashboard']} items={menuItems} />
          </div>
        </Sider>
      </BrowserView>
      {/* Mobile version */}
      <MobileView>
        {!collapsed && (
          <>
            <div
              style={{
                width: '100%',
                padding: '12px 16px',
                backgroundColor: '#FFFFFF',
              }}>
              <SearchBar />
            </div>
            <Divider style={{ margin: 0 }} />
            <ConfigProvider
              theme={{
                components: {
                  Menu: {
                    colorPrimary: '#316BFF',
                    colorText: '#316BFF',
                    colorTextLightSolid: '#316BFF',
                    colorBgContainer: '#FFFFFF',
                    itemBg: '#FFFFFF',
                    groupTitleColor: '#316BFF',
                    itemSelectedBg: '#FAFEFB',
                    itemSelectedColor: '#64BE79',
                  },
                },
              }}>
              <Menu selectedKeys={selectedKey ? [selectedKey] : ['dashboard']} items={menuItems} />
            </ConfigProvider>
          </>
        )}
        {showProfile && (
          <>
            <div
              style={{
                width: '100%',
                padding: '12px 16px',
                backgroundColor: '#FFFFFF',
                display: 'flex',
                justifyContent: 'space-between',
              }}>
              <div>
                <Text
                  style={{
                    fontWeight: 600,
                  }}>
                  {`Hello, ${displayName}!`}
                </Text>
              </div>
              <div>
                <Avatar size={24} draggable={false} src={avatar} style={{ cursor: 'pointer' }} />
              </div>
            </div>
            <Divider style={{ margin: 0 }} />
            <ConfigProvider
              theme={{
                components: {
                  Menu: {
                    colorPrimary: '#316BFF',
                    colorText: '#316BFF',
                    colorTextLightSolid: '#316BFF',
                    colorBgContainer: '#FFFFFF',
                    itemBg: '#FFFFFF',
                    groupTitleColor: '#316BFF',
                    itemSelectedBg: '#FAFEFB',
                    itemSelectedColor: '#64BE79',
                  },
                },
              }}>
              <Menu
                selectedKeys={[]}
                items={profileMenuItems}
                onClick={({ key }) => {
                  if (key === 'notification') {
                    showComingSoon()
                  }
                }}
              />
              <Divider style={{ margin: 0 }} />
              <div
                style={{
                  width: '100%',
                  padding: '12px 20px',
                  backgroundColor: '#FFFFFF',
                  display: 'flex',
                  gap: '10px',
                }}>
                <div>
                  <LogoutOutlined />
                </div>
                <div
                  onClick={() => {
                    logout().finally(() => {
                      removeCookie?.('loginToken', { path: '/' })
                      setTimeout(() => { window.location.reload() }, 1)
                    })
                  }}>
                  Logout
                </div>
              </div>
            </ConfigProvider>
          </>
        )}
      </MobileView>
    </>
  )
}
